import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LegalLinks = () => {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const termsContent = `
TERMS OF SERVICE FOR MEDICAL PROFESSIONALS

Last Updated: January 2025

1. ACCEPTANCE OF TERMS
By creating an account and using Graft Counter, you agree to these Terms of Service and acknowledge that you are a licensed medical professional authorized to perform hair transplant procedures.

2. MEDICAL PROFESSIONAL REQUIREMENTS
- Valid medical license in your jurisdiction
- Specialization or training in hair transplant procedures
- Compliance with local medical regulations and standards
- Maintenance of professional liability insurance

3. PLATFORM USE
- Use the platform solely for legitimate medical purposes
- Maintain patient confidentiality at all times
- Ensure accurate patient information and analysis data
- Report any technical issues or concerns promptly

4. DATA RESPONSIBILITY
- You are responsible for obtaining proper patient consent
- Ensure compliance with HIPAA and local privacy regulations
- Maintain secure access to your account credentials
- Report any suspected data breaches immediately

5. LIMITATION OF LIABILITY
This platform is a tool to assist medical professionals. Final medical decisions and patient care remain your professional responsibility.

6. ACCOUNT TERMINATION
We reserve the right to terminate accounts that violate these terms or compromise patient safety.
  `;

  const privacyContent = `
PRIVACY POLICY FOR MEDICAL PROFESSIONALS

Last Updated: January 2025

1. INFORMATION WE COLLECT
- Professional credentials and license information
- Account registration details (name, email, clinic information)
- Patient analysis data and uploaded images
- Usage analytics and platform interaction data

2. HOW WE USE YOUR INFORMATION
- Verify medical professional credentials
- Provide graft counting and analysis services
- Improve platform functionality and accuracy
- Ensure compliance with medical standards

3. DATA PROTECTION
- All data encrypted with AES-256 encryption
- HIPAA-compliant data handling procedures
- Regular security audits and penetration testing
- Secure data centers with 24/7 monitoring

4. DATA SHARING
We DO NOT share your personal information or patient data with:
- Third-party advertisers
- Non-medical entities
- Unauthorized personnel
- Government agencies (except as legally required)

5. YOUR RIGHTS
- Access your personal data at any time
- Request data correction or deletion
- Export your patient analysis history
- Opt-out of non-essential communications

6. DATA RETENTION
- Account data: Retained while account is active
- Patient analysis data: 7 years (medical record standards)
- Deleted data: Permanently removed within 30 days

7. CONTACT US
For privacy concerns: privacy@graftcounter.com
For data requests: data@graftcounter.com
  `;

  return (
    <div className="space-y-4">
      {/* Terms and Privacy Links */}
      <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm">
        <button
          onClick={() => setShowTerms(true)}
          className="text-blue-400 hover:text-blue-300 underline transition-colors duration-200"
        >
          Terms of Service
        </button>
        <span className="hidden sm:block text-white/30">•</span>
        <button
          onClick={() => setShowPrivacy(true)}
          className="text-blue-400 hover:text-blue-300 underline transition-colors duration-200"
        >
          Privacy Policy
        </button>
      </div>

      {/* Agreement Statement */}
      <div className="text-center">
        <p className="text-xs text-slate-500 leading-relaxed">
          By creating an account, you agree to our Terms of Service and Privacy Policy.
          You confirm that you are a licensed medical professional authorized to use
          this platform for hair transplant analysis and patient care.
        </p>
      </div>

      {/* Medical Disclaimer */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={16} color="#FCD34D" className="mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-amber-300 mb-2">
              Medical Professional Disclaimer
            </h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              This platform is designed as a clinical tool to assist qualified medical professionals.
              All analysis results should be verified and interpreted by licensed practitioners.
              Final treatment decisions remain the responsibility of the attending physician.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-[#0f1a2e] border border-white/15 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl backdrop-blur-xl">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">Terms of Service</h2>
              <button
                onClick={() => setShowTerms(false)}
                className="text-white/40 hover:text-white transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <pre className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
                {termsContent}
              </pre>
            </div>
            <div className="p-6 border-t border-white/10">
              <button
                onClick={() => setShowTerms(false)}
                className="w-full py-2.5 rounded-xl border border-white/20 bg-white/5 hover:bg-white/15 text-white text-sm transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-[#0f1a2e] border border-white/15 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl backdrop-blur-xl">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">Privacy Policy</h2>
              <button
                onClick={() => setShowPrivacy(false)}
                className="text-white/40 hover:text-white transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <pre className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
                {privacyContent}
              </pre>
            </div>
            <div className="p-6 border-t border-white/10">
              <button
                onClick={() => setShowPrivacy(false)}
                className="w-full py-2.5 rounded-xl border border-white/20 bg-white/5 hover:bg-white/15 text-white text-sm transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalLinks;